import { useState, useEffect } from 'react';
import { X, Mic } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { FIRESTORE_DB } from '../firebaseConfig';
import { useAuth } from '../hooks/useAuth';

function formatThreadDate(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  if (sameDay) {
    return `Today · ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }
  if (isYesterday) return 'Yesterday';
  return d.toLocaleDateString();
}

export default function HistoryDrawer({ open, onClose }) {
  const [query_, setQuery] = useState('');
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.uid;

  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const threadsRef = collection(FIRESTORE_DB, `users/${userId}/chatThreads`);
        const q = query(threadsRef, orderBy('lastMessageAt', 'desc'), limit(50));
        const snap = await getDocs(q);
        if (cancelled) return;
        const list = [];
        snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
        setThreads(list);
      } catch (e) {
        console.error('HistoryDrawer load threads failed:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, userId]);

  const filtered = threads.filter((t) =>
    (t.title || '').toLowerCase().includes(query_.toLowerCase())
  );

  const handleOpen = (threadId) => {
    onClose?.();
    navigate('/f8chat', { state: { threadId } });
  };

  return (
    <>
      <div
        className={`history-backdrop${open ? ' open' : ''}`}
        onClick={onClose}
      />
      <div className={`history-drawer${open ? ' open' : ''}`}>
        <div className="history-header">
          <div className="history-title">Chat history</div>
          <button className="history-close-btn" onClick={onClose} aria-label="Close history">
            <X size={14} />
          </button>
        </div>

        <div className="history-search-wrap">
          <input
            className="history-search"
            value={query_}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search history..."
          />
        </div>

        <div className="history-list">
          {loading && <div style={{ padding: 16, color: 'rgba(220,200,240,0.5)', fontSize: 12 }}>Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div style={{ padding: 16, color: 'rgba(220,200,240,0.5)', fontSize: 12 }}>
              {threads.length === 0 ? 'No chat history yet.' : 'No matches.'}
            </div>
          )}
          {filtered.map((t) => {
            const isVoice = t.type === 'voice' || /voice/i.test(t.title || '');
            return (
              <button
                key={t.id}
                className="history-item"
                onClick={() => handleOpen(t.id)}
              >
                {isVoice && (
                  <div className="history-voice-icon">
                    <Mic size={12} color="#f0abfc" />
                  </div>
                )}
                <div className="history-item-content">
                  <div className="history-item-title">{t.title || 'Untitled chat'}</div>
                  <div className="history-item-meta">
                    {formatThreadDate(t.lastMessageAt)}
                    {typeof t.messageCount === 'number' ? ` · ${t.messageCount} msg` : ''}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
